import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getAccessToken } from "../../awsAuth";

import "../Feed/Feed.scss";
import "./SelectedCollection.scss";

import ListingCard from "../Feed/Listing/ListingCard";
import PostCard from "../Feed/Post/PostCard";
import FeedActions from "../Feed/FeedActions";
import ListingDetails from "../CreateListing/ListingDetails";
import emptyIllustration from "./img/empty.png";

function SaveToCollectionModal({ open, onClose, userId, currentCollectionId, onConfirm, API }) {
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState(currentCollectionId || "all");
  const [busy, setBusy] = useState(false);

  const loadCols = useCallback(async () => {
    if (!open || !userId) return;
    try {
      const token = await getAccessToken().catch(() => null);
      const res = await axios.get(`${API}/api/saved/collections/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const cols = Array.isArray(res.data) ? res.data : [];
      setCollections(cols);
      setSelected(
        cols.find((c) => c.collectionId === currentCollectionId)
          ? currentCollectionId
          : "all"
      );
    } catch {
      setCollections([{ collectionId: "all", name: "Усі" }]);
      setSelected("all");
    }
  }, [open, userId, currentCollectionId, API]);

  useEffect(() => {
    loadCols();
  }, [loadCols]);

  if (!open) return null;

  return (
    <div className="sel-modal__backdrop" onClick={onClose}>
      <div className="sel-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sel-modal__head">
          <h3>Зберегти до колекції</h3>
          <button className="sel-modal__close" onClick={onClose}>✕</button>
        </div>
        <div className="sel-modal__list">
          {collections.map((c) => (
            <button
              key={c.collectionId}
              className={`sel-modal__item ${selected === c.collectionId ? "is-selected" : ""}`}
              onClick={() => setSelected(c.collectionId)}
            >
              {c.name}
            </button>
          ))}
        </div>
        <div className="sel-modal__footer">
          <button
            className="sel-btn sel-btn--primary"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              Promise.resolve(onConfirm(selected)).finally(() => setBusy(false));
            }}
          >
            Зберегти сюди
          </button>
        </div>
      </div>
    </div>
  );
}

const SAVED_VIEW_CACHE_TTL = 3 * 60 * 1000;

const getSavedViewCacheKey = (userId, collectionId) => {
  if (!userId) return null;
  const col = collectionId || "all";
  return `cv:savedView:${String(userId).trim()}:${String(col).trim()}`;
};

const readSavedViewCache = (userId, collectionId) => {
  try {
    if (typeof window === "undefined") return null;
    const key = getSavedViewCacheKey(userId, collectionId);
    if (!key) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (typeof parsed.ts !== "number") return null;
    if (Date.now() - parsed.ts > SAVED_VIEW_CACHE_TTL) return null;
    return {
      items: Array.isArray(parsed.items) ? parsed.items : [],
      savedIds: Array.isArray(parsed.savedIds) ? parsed.savedIds : [],
      likedItems: Array.isArray(parsed.likedItems) ? parsed.likedItems : [],
      likesCount:
        parsed.likesCount && typeof parsed.likesCount === "object"
          ? parsed.likesCount
          : {},
    };
  } catch {
    return null;
  }
};

const writeSavedViewCache = (
  userId,
  collectionId,
  { items, savedIds, likedItems, likesCount }
) => {
  try {
    if (typeof window === "undefined") return;
    const key = getSavedViewCacheKey(userId, collectionId);
    if (!key) return;
    const payload = {
      ts: Date.now(),
      items: items || [],
      savedIds: savedIds || [],
      likedItems: likedItems || [],
      likesCount: likesCount || {},
    };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {}
};

const SelectedCollection = ({
  API,
  userId,
  collectionId,
  onBack,
  onDeleteCollection,
  refreshCollections,
}) => {
  const DEFAULT_COLLECTION_ID = "all";

  const initialCache = readSavedViewCache(userId, collectionId);

  const [items, setItems] = useState(initialCache?.items || []);
  const [savedIds, setSavedIds] = useState(initialCache?.savedIds || []);
  const [likedItems, setLikedItems] = useState(initialCache?.likedItems || []);
  const [likesCount, setLikesCount] = useState(initialCache?.likesCount || {});
  const [loading, setLoading] = useState(!initialCache);
  const [error, setError] = useState("");

  const [modalListingId, setModalListingId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [pendingSaveItem, setPendingSaveItem] = useState(null);

  const clickTimerRef = useRef(null);

  const navigateHook = useNavigate();

  const safeNavigate = useCallback(
    (to) => {
      if (typeof navigateHook === "function") {
        try {
          navigateHook(to);
          return;
        } catch {}
      }
      if (typeof window !== "undefined" && to) {
        window.location.href = to;
      }
    },
    [navigateHook]
  );

  const DEFAULT_AVATAR =
    "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

  const formatTimeAgo = (ts) => {
    const d = typeof ts === "number" ? new Date(ts) : new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMin / 60);
    const diffD = Math.floor(diffH / 24);
    if (diffMin < 1) return "щойно";
    if (diffMin < 60) return `${diffMin} хв тому`;
    if (diffH < 24) return `${diffH} год тому`;
    if (diffD === 1) return "вчора";
    const months = [
      "січня","лютого","березня","квітня","травня","червня",
      "липня","серпня","вересня","жовтня","листопада","грудня",
    ];
    return `${d.getDate()} ${months[d.getMonth()]}`;
  };

  const hydrateAuthor = useCallback(
    async (raw) => {
      const item = { ...raw };
      const authorId =
        item.userId ||
        item.ownerId ||
        item.authorId ||
        item.user_id ||
        item.author_id ||
        null;

      if (item.authorName && item.authorAvatar) return item;

      if (!authorId) {
        item.authorName = item.authorName || "Користувач";
        item.authorAvatar = item.authorAvatar || DEFAULT_AVATAR;
        return item;
      }

      try {
        const acc = await axios.get(
          `${API}/api/account/${encodeURIComponent(authorId)}`
        );
        const u = acc?.data || {};
        item.authorName = item.authorName || u.username || "Користувач";
        item.authorAvatar = item.authorAvatar || u.avatar || DEFAULT_AVATAR;
        item.userId = authorId;
      } catch {
        item.authorName = item.authorName || "Користувач";
        item.authorAvatar = item.authorAvatar || DEFAULT_AVATAR;
        item.userId = authorId || item.userId;
      }
      return item;
    },
    [API]
  );

  const syncCache = useCallback(
    (nextItems, nextSavedIds, nextLikedItems, nextLikesCount) => {
      if (!userId) return;
      writeSavedViewCache(userId, collectionId, {
        items: nextItems,
        savedIds: nextSavedIds,
        likedItems: nextLikedItems,
        likesCount: nextLikesCount,
      });
    },
    [userId, collectionId]
  );

  const apiUrl = useCallback(
    (path) => {
      const base = String(API || "").replace(/\/+$/, "");
      if (!path) return base;
      if (/^https?:\/\//i.test(path)) return path;
      const p = String(path || "");
      return p.startsWith("/") ? `${base}${p}` : `${base}/${p}`;
    },
    [API]
  );

    const getAuthHeaders = useCallback(async () => {
    try {
      const token = await getAccessToken();

      return token
        ? { Authorization: `Bearer ${token}` }
        : {};
    } catch {
      return {};
    }
  }, []);

  const itemsRef = useRef(items);
  const savedIdsRef = useRef(savedIds);
  const likedItemsRef = useRef(likedItems);
  const likesCountRef = useRef(likesCount);

  useEffect(() => { itemsRef.current = items; }, [items]);
  useEffect(() => { savedIdsRef.current = savedIds; }, [savedIds]);
  useEffect(() => { likedItemsRef.current = likedItems; }, [likedItems]);
  useEffect(() => { likesCountRef.current = likesCount; }, [likesCount]);

  const likesMutationRef = useRef({ ts: 0, expectedAbs: null, expectedLiked: null, id: null });
  const LIKES_GUARD_MS = 2500;

  const likesFetchRef = useRef(0);

    const fetchLikesForIds = useCallback(
    async (ids = []) => {
      const startedAt = Date.now();
      likesFetchRef.current = startedAt;

      const nextLikes = {};
      const nextLikedByUserSet = new Set();
      const headers = await getAuthHeaders();

      await Promise.all(
        (ids || []).map(async (pid) => {
          if (!pid) return;

          try {
            const res = await fetch(
              apiUrl(`/api/likes/${encodeURIComponent(pid)}?_ts=${Date.now()}`),
              {
                cache: "no-store",
                headers,
              }
            );

            if (!res.ok) return;

            const json = await res.json().catch(() => ({}));
            if (likesFetchRef.current !== startedAt) return;

            const count = Number(json?.count ?? 0);
            nextLikes[pid] = Number.isFinite(count) ? count : 0;

            if (userId && Array.isArray(json?.users) && json.users.includes(userId)) {
              nextLikedByUserSet.add(pid);
            }
          } catch {}
        })
      );

      if (likesFetchRef.current !== startedAt) return;

      const m = likesMutationRef.current || {};
      const inGuard = m.ts && Date.now() - m.ts < LIKES_GUARD_MS;

      setLikesCount((prev) => {
        const cur = prev || {};
        const merged = { ...cur };

        for (const pid of Object.keys(nextLikes)) {
          const serverAbs = Math.max(0, Number(nextLikes[pid] || 0));

          if (
            inGuard &&
            m.id === pid &&
            Number.isFinite(m.expectedAbs) &&
            serverAbs !== m.expectedAbs
          ) {
            continue;
          }

          merged[pid] = serverAbs;
        }

        try {
          syncCache(itemsRef.current, savedIdsRef.current, likedItemsRef.current, merged);
        } catch {}

        return merged;
      });

      if (userId) {
        setLikedItems((prev) => {
          const prevSet = new Set(prev || []);

          for (const pid of ids) {
            const serverLiked = nextLikedByUserSet.has(pid);

            if (
              inGuard &&
              m.id === pid &&
              typeof m.expectedLiked === "boolean" &&
              serverLiked !== m.expectedLiked
            ) {
              continue;
            }

            if (serverLiked) prevSet.add(pid);
            else prevSet.delete(pid);
          }

          const mergedLiked = Array.from(prevSet);

          try {
            syncCache(itemsRef.current, savedIdsRef.current, mergedLiked, likesCountRef.current);
          } catch {}

          return mergedLiked;
        });
      }
    },
    [apiUrl, userId, syncCache, getAuthHeaders]
  );

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      const pid = detail.postId || detail.listingId || detail.id;
      if (!pid) return;

      const abs = Number(detail.absolute);
      const delta = Number(detail.delta || 0);

      if (Number.isFinite(abs)) {
        setLikesCount((prev) => {
          const next = { ...(prev || {}), [pid]: Math.max(0, abs) };
          syncCache(itemsRef.current, savedIdsRef.current, likedItemsRef.current, next);
          return next;
        });

        if (typeof detail.liked === "boolean" && userId) {
          setLikedItems((prev) => {
            const s = new Set([...(prev || [])]);
            if (detail.liked) s.add(pid);
            else s.delete(pid);
            const nextLiked = Array.from(s);
            syncCache(itemsRef.current, savedIdsRef.current, nextLiked, likesCountRef.current);
            return nextLiked;
          });
        }
        return;
      }

      if (delta) {
        setLikesCount((prev) => {
          const cur = Number(prev?.[pid] || 0);
          const nextAbs = Math.max(0, cur + delta);
          const next = { ...(prev || {}), [pid]: nextAbs };
          syncCache(itemsRef.current, savedIdsRef.current, likedItemsRef.current, next);
          return next;
        });

        if (typeof detail.liked === "boolean" && userId) {
          setLikedItems((prev) => {
            const s = new Set([...(prev || [])]);
            if (detail.liked) s.add(pid);
            else s.delete(pid);
            const nextLiked = Array.from(s);
            syncCache(itemsRef.current, savedIdsRef.current, nextLiked, likesCountRef.current);
            return nextLiked;
          });
        }
      }
    };

    window.addEventListener("cv-likes-updated", handler);
    return () => window.removeEventListener("cv-likes-updated", handler);
  }, [userId, syncCache]);

  const loadSaved = useCallback(
    async ({ force = false } = {}) => {
      if (!userId) return;

      setError("");

      const savedAuthHeaders = await getAuthHeaders();

      try {
  const batchRes = await axios.get(
    `${API}/api/saved/view/${userId}`,
    {
      params: {
        collectionId: collectionId || DEFAULT_COLLECTION_ID,
      },
      headers: savedAuthHeaders,
      validateStatus: () => true,
    }
  );

  if (batchRes.status === 200 && batchRes.data?.items) {
    const {
      items: batchItems,
      savedIds: batchSavedIds,
      likedItems: batchLikedItems,
      likesCount: batchLikesCount,
    } = batchRes.data;

    const baseItems = Array.isArray(batchItems) ? batchItems : [];
    const baseSavedIds = Array.isArray(batchSavedIds) ? batchSavedIds : [];

    let mergedItems = [...baseItems];
    let mergedSavedIds = [...baseSavedIds];

    try {
      const col = collectionId || DEFAULT_COLLECTION_ID;

      const rawUrl =
        col && col !== DEFAULT_COLLECTION_ID
          ? `${API}/api/saved/${userId}?collectionId=${encodeURIComponent(col)}`
          : `${API}/api/saved/${userId}`;

      const rawRes = await axios.get(rawUrl, { headers: savedAuthHeaders, validateStatus: () => true });

      if (rawRes.status >= 200 && rawRes.status < 300 && Array.isArray(rawRes.data)) {
        const rawSaved = rawRes.data;

        const normId = (s) => s?.postId || s?.listingId || s?.itemId || s?.id || s?._id || null;
        const normType = (s) => {
          const t = String(s?.type || s?.contentType || "").toLowerCase();
          if (t === "post") return "post";
          if (t === "listing") return "listing";
          if (s?.postId && !s?.listingId) return "post";
          return "listing";
        };

        mergedSavedIds = rawSaved.map(normId).filter(Boolean);

        const existingIds = new Set(
          mergedItems
            .map((it) => (it?.type === "listing" ? (it.listingId || it.id) : (it.postId || it.id)))
            .filter(Boolean)
        );

        const missingPostIds = rawSaved
          .filter((s) => normType(s) === "post")
          .map(normId)
          .filter((id) => id && !existingIds.has(id));

        if (missingPostIds.length) {
          const fetchedPosts = await Promise.all(
            missingPostIds.map(async (pid) => {
              try {
                const resp = await axios.get(
                  `${API}/api/posts/${encodeURIComponent(pid)}`,
                  { validateStatus: () => true }
                );
                if (resp.status === 404) return null;
                if (resp.status < 200 || resp.status >= 300 || !resp.data) return null;

                const data = resp.data || {};
                data.type = "post";
                data.postId = data.postId || data.id || pid;

                const enriched = await hydrateAuthor(data);
                return enriched;
              } catch {
                return null;
              }
            })
          );

          const goodPosts = fetchedPosts.filter(Boolean);

          if (goodPosts.length) {
            mergedItems = [...mergedItems, ...goodPosts];
            mergedItems.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
          }
        }
      }
    } catch {}

    setItems(mergedItems || []);
    setSavedIds(mergedSavedIds || []);
    setLikedItems(batchLikedItems || []);
    setLikesCount(batchLikesCount || {});
    syncCache(
      mergedItems || [],
      mergedSavedIds || [],
      batchLikedItems || [],
      batchLikesCount || {}
    );
    setLoading(false);

    try {
      const ids = (mergedItems || [])
        .map((it) => (it?.type === "listing" ? (it.listingId || it.id) : (it.postId || it.id)))
        .filter(Boolean);
      fetchLikesForIds(ids);
    } catch {}

    return;
  }
} catch {
}


      if (!force) {
        const cached = readSavedViewCache(userId, collectionId);
        if (cached && cached.items?.length) {
          setItems(cached.items);
          setSavedIds(
            cached.savedIds ||
              cached.items.map((i) => i.postId || i.listingId)
          );
          setLikedItems(cached.likedItems || []);
          setLikesCount(cached.likesCount || {});
          setLoading(false);

          try {
            const ids = (cached.items || [])
              .map((it) => (it?.type === "listing" ? (it.listingId || it.id) : (it.postId || it.id)))
              .filter(Boolean);
            fetchLikesForIds(ids);
          } catch {}

          return;
        }
      }

      setLoading(true);

      try {
        const url =
          collectionId && collectionId !== DEFAULT_COLLECTION_ID
            ? `${API}/api/saved/${userId}?collectionId=${encodeURIComponent(
                collectionId
              )}`
            : `${API}/api/saved/${userId}`;
        const savedRes = await axios.get(url, { headers: savedAuthHeaders });
        const saved = Array.isArray(savedRes.data) ? savedRes.data : [];

        

        if (!saved.length) {
          setItems([]);
          setSavedIds([]);
          setLikedItems([]);
          setLikesCount({});
          syncCache([], [], [], {});
          setLoading(false);
          return;
        }

        const savedIdsList = saved.map((s) => s.postId);
        setSavedIds(savedIdsList);

        const detailResults = await Promise.all(
          saved.map(async (s) => {
            const type = s.type === "post" ? "post" : "listing";
            const id = s.postId;
            try {
              const endpoint =
                type === "post"
                  ? `${API}/api/posts/${id}`
                  : `${API}/api/listings/${id}`;
              const resp = await axios.get(endpoint, {
                validateStatus: () => true,
              });
              if (resp.status === 404) {
                return { ok: false, id, type, isNotFound: true };
              }
              if (resp.status < 200 || resp.status >= 300 || !resp.data) {
                return { ok: false, id, type };
              }
              let data = resp.data;
              if (type === "listing") {
                data.type = "listing";
                data.listingId = data.listingId || data.id || id;
              } else {
                data.type = "post";
                data.postId = data.postId || data.id || id;
              }
              const enriched = await hydrateAuthor(data);
              return { ok: true, id, type, data: enriched };
            } catch {
              return { ok: false, id, type };
            }
          })
        );

        const detailed = [];
        const brokenSavedIds = [];

        for (const r of detailResults) {
          if (!r) continue;
          if (r.ok && r.data) {
            detailed.push(r.data);
          } else if (r.isNotFound) {
            brokenSavedIds.push({ postId: r.id, type: r.type });
          }
        }

        if (brokenSavedIds.length) {
          try {
            await Promise.all(
              brokenSavedIds.map((b) =>
                axios.delete(`${API}/api/saved`, {
                  data: {
                    userId,
                    postId: b.postId,
                    type: b.type || "listing",
                  },
                  headers: savedAuthHeaders,
                })
              )
            );
          } catch {}
        }

        detailed.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setItems(detailed);

        const ids = detailed
          .map((it) => (it.type === "listing" ? (it.listingId || it.id) : (it.postId || it.id)))
          .filter(Boolean);

        // старий блок лишаємо
        const likesData = {};
        const likedByUser = [];

        await Promise.all(
          detailed.map(async (it) => {
            const pid = it.type === "listing" ? it.listingId : it.postId;
            try {
              const headers = await getAuthHeaders();

              const res = await axios.get(`${API}/api/likes/${pid}`, {
                headers,
                validateStatus: () => true,
              });
              if (res.status >= 200 && res.status < 300) {
                likesData[pid] = res.data?.count ?? 0;
                if (
                  Array.isArray(res.data?.users) &&
                  res.data.users.includes(userId)
                ) {
                  likedByUser.push(pid);
                }
              }
            } catch {}
          })
        );

        setLikesCount(likesData);
        setLikedItems(likedByUser);

        syncCache(detailed, savedIdsList, likedByUser, likesData);

        fetchLikesForIds(ids);
      } catch {
        setError("Не вдалося завантажити збережене");
      } finally {
        setLoading(false);
      }
    },
    [API, userId, collectionId, DEFAULT_COLLECTION_ID, hydrateAuthor, syncCache, fetchLikesForIds]
  );

  useEffect(() => {
    loadSaved({ force: false });
  }, [loadSaved]);

    const toggleLike = async (item) => {
    const id = item.type === "listing" ? item.listingId : item.postId;
    if (!id) return;

    const currentlyLiked = likedItemsRef.current.includes(id);
    const curAbs = Number(likesCountRef.current?.[id] || 0);
    const expectedAbs = currentlyLiked ? Math.max(0, curAbs - 1) : curAbs + 1;
    const expectedLiked = !currentlyLiked;

    likesMutationRef.current = { ts: Date.now(), expectedAbs, expectedLiked, id };

    try {
      setLikesCount((prev) => {
        const next = { ...(prev || {}), [id]: expectedAbs };
        syncCache(itemsRef.current, savedIdsRef.current, likedItemsRef.current, next);
        return next;
      });

      setLikedItems((prev) => {
        const s = new Set([...(prev || [])]);

        if (expectedLiked) s.add(id);
        else s.delete(id);

        const nextLiked = Array.from(s);

        syncCache(itemsRef.current, savedIdsRef.current, nextLiked, likesCountRef.current);

        return nextLiked;
      });

      try {
        window.dispatchEvent(
          new CustomEvent("cv-likes-updated", {
            detail: {
              id,
              postId: id,
              listingId: id,
              liked: expectedLiked,
              delta: currentlyLiked ? -1 : 1,
              absolute: expectedAbs,
            },
          })
        );
      } catch {}

      const headers = await getAuthHeaders();

      if (currentlyLiked) {
        await axios.delete(`${API}/api/likes`, {
          headers,
          data: { postId: id, userId },
        });
      } else {
        await axios.post(
          `${API}/api/likes`,
          { postId: id, userId },
          { headers }
        );
      }

      setTimeout(() => {
        fetchLikesForIds([id]);
      }, 220);
    } catch {}
  };

  const toggleSave = async (item) => {
    const id = item.type === "listing" ? item.listingId : item.postId;
    try {
      if (savedIds.includes(id)) {
        const headers = await getAuthHeaders();
        await axios.delete(`${API}/api/saved`, {
          data: { userId, postId: id, type: item.type },
          headers,
        });
        const nextSavedIds = savedIds.filter((x) => x !== id);
        const nextItems = items.filter(
          (i) => (i.postId || i.listingId) !== id
        );
        setSavedIds(nextSavedIds);
        setItems(nextItems);
        syncCache(nextItems, nextSavedIds, likedItems, likesCount);
        await refreshCollections?.();
      } else {
        setPendingSaveItem({ ...item, _saveId: id });
        setSaveModalOpen(true);
      }
    } catch {}
  };

  const confirmSaveToCollection = async (newCollectionId) => {
    if (!pendingSaveItem) return;
    const id = pendingSaveItem._saveId;
    try {
      const headers = await getAuthHeaders();
      await axios.post(`${API}/api/saved`, {
        userId,
        postId: id,
        type: pendingSaveItem.type,
        collectionId: newCollectionId || "all",
      }, { headers });
      const nextSavedIds = savedIds.includes(id)
        ? savedIds
        : [...savedIds, id];
      setSavedIds(nextSavedIds);
      syncCache(items, nextSavedIds, likedItems, likesCount);
      await refreshCollections?.();
    } catch {
      alert("Не вдалося зберегти");
    } finally {
      setSaveModalOpen(false);
      setPendingSaveItem(null);
    }
  };

  const toggleLikeRef = useRef(toggleLike);
  useEffect(() => {
    toggleLikeRef.current = toggleLike;
  }, [toggleLike]);

  const handleDoubleClick = useCallback(
    (item) => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      const id = item.type === "listing" ? item.listingId : item.postId;
      if (!likedItemsRef.current.includes(id)) toggleLikeRef.current(item);
    },
    []
  );

  const handleSingleClick = useCallback(
    (item) => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      clickTimerRef.current = setTimeout(() => {
        if (item.type === "listing") {
          const lid = item.listingId;
          safeNavigate(`/listing/${lid}`);
          setModalListingId(lid);
        }
        clickTimerRef.current = null;
      }, 180);
    },
    [safeNavigate]
  );

  const isAll = collectionId === DEFAULT_COLLECTION_ID;

  return (
    <div className="selected">
      {loading && (
        <div className="selected__skeleton">
          {Array.from({ length: 4 }).map((_, i) => (
            <div className="selected__skeletonCard shimmer" key={`sk-${i}`}>
              <div className="selected__skeletonThumb" />
              <div className="selected__skeletonLines">
                <div className="line w70" />
                <div className="line w50" />
              </div>
            </div>
          ))}
        </div>
      )}

      {error && !loading && <p className="feed__empty">{error}</p>}

      {!loading && !error && items.length === 0 && (
        <div className="selected__empty">
          <p className="feed__empty">Поки що нічого не збережено</p>
          <img className="selected__emptyImg" src={emptyIllustration} alt="" />
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="feed selected-feed">
          {items.map((item) => {
            const id = item.type === "listing" ? item.listingId : item.postId;
            const isSaved = savedIds.includes(id);
            const isLiked = likedItems.includes(id);
            const timeAgo = formatTimeAgo(item.createdAt);

            return (
              <div
                key={id}
                className={`feed__card ${
                  item.type === "post" ? "feed__card--post" : ""
                }`}
                onDoubleClick={() => handleDoubleClick(item)}
                onClick={() => handleSingleClick(item)}
              >
                {item.type === "listing" ? (
                  <ListingCard
                    item={item}
                    isSaved={isSaved}
                    isLiked={isLiked}
                    likesCount={Number(likesCount?.[id] || 0)}
                    onToggleLike={() => toggleLike(item)}
                    onToggleSave={() => toggleSave(item)}
                    onOpenActions={() =>
                      setSelectedItem({
                        id,
                        type: item.type,
                        createdAt: item.createdAt,
                        userId: item.userId,
                      })
                    }
                    onOpenListingDetails={() =>
                      safeNavigate(`/listing/${item.listingId}`)
                    }
                    onDoubleLike={(it) => {
                      if (!likedItemsRef.current.includes(id)) toggleLike(it);
                    }}
                    timeAgo={timeAgo}
                  />
                ) : (
                  <PostCard
                    item={item}
                    isSaved={isSaved}
                    isLiked={isLiked}
                    likesCount={Number(likesCount?.[id] || 0)}
                    onToggleLike={() => toggleLike(item)}
                    onToggleSave={() => toggleSave(item)}
                    onOpenActions={() =>
                      setSelectedItem({
                        id,
                        type: item.type,
                        createdAt: item.createdAt,
                        userId: item.userId,
                      })
                    }
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedItem && (
        <FeedActions
          type={selectedItem.type}
          id={selectedItem.id}
          createdAt={selectedItem.createdAt}
          userId={selectedItem.userId}
          isOwner={selectedItem.userId === userId}
          onClose={() => setSelectedItem(null)}
          onDelete={() => {}}
          onArchive={() => {}}
        />
      )}

      {modalListingId && (
        <ListingDetails
          listingId={modalListingId}
          isModal={true}
          onClose={() => {
            setModalListingId(null);
            try {
              window.history.length > 1 && window.history.back();
            } catch {}
          }}
        />
      )}

      <SaveToCollectionModal
        open={saveModalOpen}
        onClose={() => {
          setSaveModalOpen(false);
          setPendingSaveItem(null);
        }}
        onConfirm={async (cid) => {
          await confirmSaveToCollection(cid);
        }}
        userId={userId}
        currentCollectionId={collectionId}
        API={API}
      />
    </div>
  );
};

export default SelectedCollection;
